import React, { useState, useEffect, useMemo } from "react";
import {
  Typography,
  Box,
  CircularProgress,
} from "@mui/material";
import { useCoordinatesHistory } from './CoOrdinateHistory.js';
import PoweredBy_Logo from "../../images/poweredBy.svg"

//constants
const CONSTANTS = {
  STOP_RADIUS: 70, // meters - radius to detect if vehicle is at a stop
  STOP_VISIT_TIME: 0.01, // seconds - time needed at a stop to mark it as visited (increased from 2)
  DEPARTURE_DISTANCE: 30, // meters - distance from last stop to consider "departed" (increased from 30)
  LOOK_AHEAD_DISTANCE: 1000, // meters - maximum distance to look ahead for future stops
  DATA_STALENESS_THRESHOLD: 30000,
  EARTH_RADIUS: 6371e3,
  MAX_STOP_DISTANCE: Infinity, // No limit - stops can be at any distance
  MIN_STOP_DISTANCE: 0, // Minimum distance between stops (0m)
  AVG_SPEED: 30, // Average speed in km/h
  MIN_STOP_TIME: 0.1, // Minimum time between stops in seconds
  MAX_STOP_TIME: 100, // Maximum time between stops in seconds
  TRAFFIC_MULTIPLIER: 1.1, // 10% buffer for traffic
  U_TURN_DETECTION_RADIUS: 40, // meters - radius to detect vehicle returning to visited stop
  U_TURN_CHECK_COUNT: 2, // number of recently visited stops to check for u-turn
  ARRIVAL_DISTANCE: 70, // meters - distance to show "Arrived"
  ARRIVAL_TIME: 10, // seconds - time threshold to show "Arrived"
  STOP_PASS_TOLERANCE: 70, // meters - distance to keep stop valid after passing route index
};

const calculateRealisticETA = (distance) => {
  const distanceInKm = distance / 1000;
  let baseTime = (distanceInKm / CONSTANTS.AVG_SPEED) * 3600;
  baseTime *= CONSTANTS.TRAFFIC_MULTIPLIER;
  const numTrafficLights = Math.floor(distance / 800);
  const avgTrafficLightDelay = 20;
  baseTime += numTrafficLights * avgTrafficLightDelay;
  return Math.min(
    Math.max(Math.round(baseTime), CONSTANTS.MIN_STOP_TIME),
    CONSTANTS.MAX_STOP_TIME
  );
};

const calculateImprovedDistance = (pos1, pos2) => {
  if (!pos1?.lat || !pos1?.lng || !pos2?.latitude || !pos2?.longitude)
    return Infinity;

  try {
    const φ1 = (pos1.lat * Math.PI) / 180;
    const φ2 = (parseFloat(pos2.latitude) * Math.PI) / 180;
    const Δφ = ((parseFloat(pos2.latitude) - pos1.lat) * Math.PI) / 180;
    const Δλ = ((parseFloat(pos2.longitude) - pos1.lng) * Math.PI) / 180;

    const a =
      Math.sin(Δφ / 2) * Math.sin(Δφ / 2) +
      Math.cos(φ1) * Math.cos(φ2) * Math.sin(Δλ / 2) * Math.sin(Δλ / 2);
    const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));

    const distance = CONSTANTS.EARTH_RADIUS * c;

    return Math.min(
      Math.max(distance, CONSTANTS.MIN_STOP_DISTANCE),
      CONSTANTS.MAX_STOP_DISTANCE
    );
  } catch (error) {
    console.error("Error calculating distance:", error);
    return Infinity;
  }
};

const formatTime = (timeString) => {
  if (!timeString) return "---";

  if (
    typeof timeString === "string" &&
    (timeString.includes("m") || timeString.includes("s"))
  ) {
    return timeString;
  }

  const seconds =
    typeof timeString === "string" ? parseInt(timeString) : timeString;
  if (isNaN(seconds) || seconds <= 0) return "---";

  const hours = Math.floor(seconds / 3600);
  const minutes = Math.floor((seconds % 3600) / 60);
  const secs = seconds % 60;

  if (hours > 0) return `${hours}h ${minutes}m`;
  if (minutes > 0) return `${minutes}m ${secs}s`;
  return `${secs}s`;
};

const NextStopTableScreenScreen = ({
  details,
  stops = [],
  stopDetails = {},
  isInbound,
  currentPosition,
  routeCoordinates = [],
}) => {
  // console.log("📍 [NextStop] Component rendered with isInbound:", isInbound, "Position:", currentPosition);
  // State for visited stops tracking
  const [visitedStops, setVisitedStops] = useState([]);
  const [stopVisitTimers, setStopVisitTimers] = useState({});
  const [currentStopId, setCurrentStopId] = useState(null);
  const [previousData, setPreviousData] = useState({
    nextStop: null,
    stopDetail: null,
    lastUpdateTime: null,
  });
  const [lastStopDistance, setLastStopDistance] = useState(null);
  const [lastVisitedStop, setLastVisitedStop] = useState(null);
  const { recentCoordinates, addCoordinate, clearHistory } = useCoordinatesHistory();

  const getBestMetrics = (
    calculatedDistance,
    backendDistance,
    calculatedETA,
    backendETA
  ) => {
    let finalDistance = calculatedDistance;
    let finalETA = calculatedETA;

    if (backendDistance) {
      const backendInMeters =
        backendDistance > 100 ? backendDistance : backendDistance * 1000;

      if (calculatedDistance && backendInMeters) {
        const diff = Math.abs(calculatedDistance - backendInMeters);
        if (diff / calculatedDistance < 0.5) {
          finalDistance = Math.min(calculatedDistance, backendInMeters);
        } else {
          finalDistance = calculatedDistance;
        }
      }
    }

    if (backendETA && calculatedETA) {
      const diff = Math.abs(calculatedETA - backendETA);
      if (diff / calculatedETA < 0.5) {
        finalETA = Math.min(calculatedETA, backendETA);
      } else {
        finalETA = calculatedETA;
      }
    }

    return {
      distance: finalDistance / 1000,
      eta: finalETA,
    };
  };

  const calculateDistance = (pos1, pos2) => {
    if (!pos1?.lat || !pos1?.lng || !pos2?.latitude || !pos2?.longitude)
      return Infinity;

    try {
      const φ1 = (pos1.lat * Math.PI) / 180;
      const φ2 = (parseFloat(pos2.latitude) * Math.PI) / 180;
      const Δφ = ((parseFloat(pos2.latitude) - pos1.lat) * Math.PI) / 180;
      const Δλ = ((parseFloat(pos2.longitude) - pos1.lng) * Math.PI) / 180;

      const a =
        Math.sin(Δφ / 2) * Math.sin(Δφ / 2) +
        Math.cos(φ1) * Math.cos(φ2) * Math.sin(Δλ / 2) * Math.sin(Δλ / 2);
      const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));

      return CONSTANTS.EARTH_RADIUS * c;
    } catch (error) {
      console.error("Error calculating distance:", error);
      return Infinity;
    }
  };

  const findRouteIndex = (position) => {
    if (!position || !routeCoordinates?.length) return -1;

    let minDistance = Infinity;
    let closestIndex = -1;

    routeCoordinates.forEach((coord, index) => {
      const distance = calculateDistance(position, {
        latitude: coord.latitude,
        longitude: coord.longitude,
      });

      if (distance < minDistance) {
        minDistance = distance;
        closestIndex = index;
      }
    });

    return closestIndex;
  };

  // Log visited stops state for debugging
  // console.log("✅ [Visited Stops]:", visitedStops.map(v => v.name).join(", ") || "None");
  
  // Reset states when direction changes
  useEffect(() => {
    // console.log("🔄 [Direction Change] isInbound changed to:", isInbound, "- Resetting all tracking state");
    setVisitedStops([]);
    setStopVisitTimers({});
    setCurrentStopId(null);
    setLastVisitedStop(null);
    setLastStopDistance(null);
    setPreviousData({
      nextStop: null,
      stopDetail: null,
      lastUpdateTime: null,
    });
    clearHistory();
  }, [isInbound]);

  // Determine next stop
  const { nextStop, stopDetail } = useMemo(() => {
    try {
      if (!currentPosition || !stops?.length || !routeCoordinates?.length) {
        return previousData;
      }

      const currentIndex = findRouteIndex(currentPosition);
      if (currentIndex === -1) return previousData;

      // Check distance from last visited stop
      let distanceFromLastStop = Infinity;
      if (lastVisitedStop) {
        distanceFromLastStop = calculateImprovedDistance(
          currentPosition,
          lastVisitedStop
        );
        setLastStopDistance(distanceFromLastStop);
      }

      if (distanceFromLastStop < CONSTANTS.DEPARTURE_DISTANCE) {
        return previousData;
      }

      // Create a Set of visited stop IDs for faster lookup
      const visitedStopIds = new Set(visitedStops.map(v => v.stopId));
      // console.log("🚫 [Filtering] Excluding visited stop IDs:", Array.from(visitedStopIds));

      // Find all unvisited stops
      const stopsWithData = stops
        .filter((stop) => !visitedStopIds.has(stop.stopId))
        .map((stop) => ({
          ...stop,
          routeIndex: findRouteIndex({
            lat: parseFloat(stop.latitude),
            lng: parseFloat(stop.longitude),
          }),
          distance: calculateImprovedDistance(currentPosition, stop),
        }))
        .filter((stop) => stop.routeIndex !== -1);
      
      // console.log("📋 [Unvisited Stops]:", stopsWithData.map(s => s.name).join(", ") || "None");

      // console.log("📋🔍 [Before Sort] Stops to sort:", stopsWithData.map(s => `${s.name} (dist: ${s.distance.toFixed(0)}m, idx: ${s.routeIndex})`).join(", "));
      
      // Find the absolute closest stop first
      const closestStop = stopsWithData.reduce((closest, stop) => 
        !closest || stop.distance < closest.distance ? stop : closest
      , null);
      // console.log(`🎯 [Closest Stop Overall]: ${closestStop?.name} at ${closestStop?.distance.toFixed(0)}m`);
      
      const validStops = stopsWithData
        .filter((stop) => {
          const isIndexValid = isInbound
            ? stop.routeIndex <= currentIndex
            : stop.routeIndex >= currentIndex;
          
          // Even if we passed the stop's route index, keep it valid if we're very close
          // This prevents skipping stops when GPS/route alignment isn't perfect
          const isCloseEnough = stop.distance <= CONSTANTS.STOP_PASS_TOLERANCE;
          
          // OVERRIDE: Always include the closest stop, regardless of route index
          const isClosestStop = closestStop && stop.stopId === closestStop.stopId;
          
          const isValid = isIndexValid || isCloseEnough || isClosestStop;
          // console.log(`  🔍 ${stop.name}: isIndexValid=${isIndexValid}, isCloseEnough=${isCloseEnough}, isClosest=${isClosestStop}, RESULT=${isValid}`);

          return isValid;
        })
        // Sort by actual distance (closest first), then by route index as tiebreaker
        .sort((a, b) => {
          const distanceDiff = a.distance - b.distance;
          // console.log(`    🔀 Comparing: ${a.name}(${a.distance.toFixed(0)}m) vs ${b.name}(${b.distance.toFixed(0)}m) → diff=${distanceDiff.toFixed(0)}m`);
          
          // If distances are very similar (within 50m), use route index to break tie
          if (Math.abs(distanceDiff) < 50) {
            const indexResult = isInbound ? b.routeIndex - a.routeIndex : a.routeIndex - b.routeIndex;
            // console.log(`      ↳ Close distances, using route index: ${indexResult > 0 ? b.name : a.name} wins`);
            return indexResult;
          }
          // console.log(`      ↳ Clear distance difference: ${distanceDiff < 0 ? a.name : b.name} wins (closer)`);
          return distanceDiff; // Otherwise, closest wins
        });

      // console.log("✔️ [After Sort] Valid Stops:", validStops.map(s => `${s.name} (dist: ${(s.distance/1000).toFixed(3)}km, idx: ${s.routeIndex})`).join(", ") || "None");
      
      if (!validStops.length) {
        // console.log("⚠️ [Next Stop] No valid stops remaining - keeping previous data");
        return previousData;
      }

      const nextValidStop = validStops[0];
      // console.log("🎯 [Next Stop] Selected:", nextValidStop.name, "Distance:", (nextValidStop.distance / 1000).toFixed(2), "km");
      
      const backendStopDetail = stopDetails[nextValidStop.stopId];

      const calculatedDistance = nextValidStop.distance;
      const calculatedETA = calculateRealisticETA(calculatedDistance);

      const bestMetrics = getBestMetrics(
        calculatedDistance,
        backendStopDetail?.distance,
        calculatedETA,
        backendStopDetail?.eta
      );

      return {
        nextStop: nextValidStop,
        stopDetail: {
          ...backendStopDetail,
          distance: bestMetrics.distance,
          eta: bestMetrics.eta,
        },
      };
    } catch (error) {
      console.error("Error determining next stop:", error);
      return previousData;
    }
  }, [
    currentPosition,
    stops,
    stopDetails,
    routeCoordinates,
    isInbound,
    visitedStops,
    lastVisitedStop,
    lastStopDistance,
  ]);

  // Update previous data
  useEffect(() => {
    if (nextStop && stopDetail) {
      setPreviousData({
        nextStop,
        stopDetail,
        lastUpdateTime: Date.now(),
      });
    }
  }, [nextStop, stopDetail]);

  // IMPROVED STOP VISIT TRACKING
  useEffect(() => {
    if (!currentPosition || !stops?.length) return;

    // Add coordinate to history
    const lastCoord = recentCoordinates[0];
    if (!lastCoord ||
      lastCoord.lat !== currentPosition.lat ||
      lastCoord.lng !== currentPosition.lng) {
      addCoordinate(currentPosition);
    }

    const visitedStopIds = new Set(visitedStops.map(v => v.stopId));

    stops.forEach((stop) => {
      const currentDistance = calculateDistance(currentPosition, stop);
      const isAtStop = currentDistance <= CONSTANTS.STOP_RADIUS;
      const isAlreadyVisited = visitedStopIds.has(stop.stopId);

      if (isAtStop && !isAlreadyVisited) {
        // Start or continue timing
        setStopVisitTimers(prev => {
          const existing = prev[stop.stopId];

          if (existing) {
            const timeSpent = (Date.now() - existing.startTime) / 1000;

            // Mark as visited if stayed long enough
            if (timeSpent >= CONSTANTS.STOP_VISIT_TIME) {
              // console.log(`✅✅✅ [VISIT CONFIRMED] Stop "${stop.name}" marked as VISITED (stayed ${timeSpent.toFixed(1)}s at ${currentDistance.toFixed(2)}m)`);
              
              setVisitedStops(current => {
                const newVisited = [...current, {
                  stopId: stop.stopId,
                  timestamp: Date.now(),
                  name: stop.name
                }];
                // console.log(`📝 [Visited Stops Updated] Now: [${newVisited.map(v => v.name).join(", ")}]`);
                return newVisited;
              });
              setLastVisitedStop(stop);
              
              // Remove timer
              const { [stop.stopId]: _, ...rest } = prev;
              return rest;
            }
            // console.log(`⏱️ [Timer] Stop "${stop.name}" - ${timeSpent.toFixed(1)}s / ${CONSTANTS.STOP_VISIT_TIME}s (distance: ${currentDistance.toFixed(2)}m)`);
            return prev;
          }
          
          // Start new timer
          // console.log(`⏱️ [Timer Started] Stop "${stop.name}" at ${currentDistance.toFixed(2)}m`);
          return {
            ...prev,
            [stop.stopId]: { startTime: Date.now() }
          };
        });
      } else {
        // Remove timer if moved away or already visited
        setStopVisitTimers(prev => {
          if (prev[stop.stopId]) {
            const reason = isAlreadyVisited ? "already visited" : "moved away";
            // console.log(`❌ [Timer Cancelled] Stop "${stop.name}" (${reason}, distance: ${currentDistance.toFixed(2)}m)`);
          }
          const { [stop.stopId]: _, ...rest } = prev;
          return rest;
        });
      }
    });
  }, [
    currentPosition,
    stops,
    visitedStops,
    recentCoordinates,
    addCoordinate
  ]);

  // Determine if data is stale
  const isDataStale = useMemo(() => {
    if (!nextStop || !stopDetail) return true;
    if (!previousData.lastUpdateTime) return false;
    return (
      Date.now() - previousData.lastUpdateTime >
      CONSTANTS.DATA_STALENESS_THRESHOLD
    );
  }, [nextStop, stopDetail, previousData.lastUpdateTime]);

  // Check if vehicle is at the next stop
  const distanceToNextStop = nextStop && currentPosition
    ? calculateImprovedDistance(currentPosition, nextStop)
    : null;

  const calculatedETAValue = distanceToNextStop !== null 
    ? calculateRealisticETA(distanceToNextStop) 
    : null;

  // Show "Arrived" when distance < 100m AND time <= 10s
  const isArrivedConditionMet = distanceToNextStop !== null && 
    distanceToNextStop < CONSTANTS.ARRIVAL_DISTANCE && 
    calculatedETAValue !== null &&
    calculatedETAValue <= CONSTANTS.ARRIVAL_TIME;

  const isAtNextStop = distanceToNextStop !== null && distanceToNextStop <= CONSTANTS.STOP_RADIUS;

  const distance = distanceToNextStop !== null
    ? isArrivedConditionMet
      ? "Arrived"
      : (distanceToNextStop / 1000).toFixed(1)
    : null;

  const eta = distanceToNextStop !== null
    ? isArrivedConditionMet
      ? "Arrived"
      : formatTime(calculatedETAValue)
    : null;

  // Mark as visited when arrival conditions are met
  useEffect(() => {
    if (isArrivedConditionMet && nextStop && !visitedStops.find(v => v.stopId === nextStop.stopId)) {
      // console.log(`✅ Stop ${nextStop.name} marked as VISITED (distance: ${distanceToNextStop.toFixed(2)}m, ETA: ${calculatedETAValue}s)`);
      
      setVisitedStops(current => [...current, {
        stopId: nextStop.stopId,
        timestamp: Date.now(),
        name: nextStop.name
      }]);
      setLastVisitedStop(nextStop);
    }
  }, [isArrivedConditionMet, nextStop, distanceToNextStop, calculatedETAValue, visitedStops]);

  return (
    <Box
      sx={{
        width: { xs: '100vw', sm: '90vw', md: '50vw', lg: '42vw' },
        minHeight: { xs: '80vh', sm: '97.6vh' },
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'space-between',
        alignItems: 'center',
        backgroundColor: '#E8EAEE',
        color: '#F9E719',
        textAlign: 'center',
        borderRadius: "20px",
        boxSizing: 'border-box',
        margin: "12px 16px 12px 0px"
      }}
    >
      <Box
        sx={{
          borderRadius: "10px 10px 0px 0px",
          width: '100%',
          zIndex: 1000,
          backgroundColor: '#183059',
          display: 'flex',
          alignItems: 'center',
          gap: '16px',
          padding: '10px 8px',
        }}
      >
        <div style={{ width: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '16px' }}>
          <svg width="42" height="42" viewBox="0 0 33 32" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path fillRule="evenodd" clipRule="evenodd" d="M3.16699 2.66699H11.167V13.3337H8.50033V29.3337H5.83366V13.3337H3.16699V2.66699ZM13.8337 26.3151V28.0003C13.8337 28.7367 14.4306 29.3337 15.167 29.3337C15.9034 29.3337 16.5003 28.7367 16.5003 28.0003V27.3337H25.8337V28.0003C25.8337 28.7367 26.4306 29.3337 27.167 29.3337C27.9034 29.3337 28.5003 28.7367 28.5003 28.0003V26.3151C29.3186 25.5827 29.8337 24.5183 29.8337 23.3337V12.3151C29.8337 10.821 28.9643 9.30236 27.3289 8.8242C25.977 8.42898 23.8842 8.00033 21.167 8.00033C18.4498 8.00033 16.357 8.42898 15.0051 8.8242C13.3697 9.30236 12.5003 10.821 12.5003 12.3151V23.3337C12.5003 24.5183 13.0153 25.5827 13.8337 26.3151ZM27.167 14.3337V17.667H15.167V14.3337H27.167ZM17.8337 23.3337C18.5701 23.3337 19.167 22.7367 19.167 22.0003C19.167 21.2639 18.5701 20.667 17.8337 20.667C17.0973 20.667 16.5003 21.2639 16.5003 22.0003C16.5003 22.7367 17.0973 23.3337 17.8337 23.3337ZM25.8337 22.0003C25.8337 22.7367 25.2367 23.3337 24.5003 23.3337C23.7639 23.3337 23.167 22.7367 23.167 22.0003C23.167 21.2639 23.7639 20.667 24.5003 20.667C25.2367 20.667 25.8337 21.2639 25.8337 22.0003Z" fill="white" />
          </svg>

          <span
            style={{
              color: '#FFFFFF',
              fontFamily: 'Roboto, sans-serif',
              fontWeight: 500,
              fontStyle: 'normal',
              fontSize: '46px',
              lineHeight: '100%',
              letterSpacing: '0%',
              whiteSpace: 'nowrap',
            }}
          >
            Next Stop
          </span>
        </div>
      </Box>

      <Box sx={{
        borderRadius: '16px',
        padding: { xs: '0.5rem', sm: '0.1rem' },
        width: { xs: '90%', sm: '95%' },
        marginBottom: { xs: '0.5rem', sm: '0.75rem' },
        height: "auto",
        minHeight: "fit-content",
        display: "flex",
        flexDirection: "column",
        gap: "20px",
      }}>
        <Box sx={{
          display: 'flex',
          justifyContent: 'space-around',
          width: { xs: '90%', sm: '99%' },
          gap: { xs: '0.5rem', sm: '1rem' },
          flexDirection: { xs: 'column', sm: 'column', md: 'column' },
          margin: "auto"
        }}>
          <Box sx={{
            backgroundColor: '#183059',
            borderRadius: '16px',
            padding: { xs: '0.5rem', sm: '1rem' },
            flex: 1,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center'
          }}>
            <Typography variant="h2"
              sx={{
                color: '#F9E719',
                fontWeight: 'bold',
                fontSize: {
                  xs: '1.5rem',
                  sm: '2rem',
                  md: '4rem',
                },
              }}>
              {nextStop?.name || "Determining Next Stop"}
            </Typography>
          </Box>

          <Box sx={{
            backgroundColor: '#183059',
            borderRadius: '16px',
            padding: { xs: '0.5rem', sm: '1rem' },
            flex: 1,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center'
          }}>
            <Typography variant="h3"
              sx={{
                color: 'white',
                fontWeight: 'bold',
                marginBottom: { xs: '0.25rem', sm: '0.5rem' },
                fontSize: {
                  xs: '1rem',
                  sm: '1.5rem',
                  md: '1.5rem',
                },
              }}>
              ETA
            </Typography>
            <Typography variant="h2"
              sx={{
                color: '#F9E719',
                fontWeight: 'bold',
                fontSize: {
                  xs: '1rem',
                  sm: '1.5rem',
                  md: '3rem',
                },
              }}>
              {eta || <CircularProgress color="inherit" />}
            </Typography>
          </Box>

          <Box sx={{
            backgroundColor: '#183059',
            borderRadius: '16px',
            padding: { xs: '0.5rem', sm: '1rem' },
            flex: 1,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
          }}>
            <Typography variant="h3"
              sx={{
                color: 'white',
                fontWeight: 'bold',
                marginBottom: { xs: '0.25rem', sm: '0.5rem' },
                fontSize: {
                  xs: '1rem',
                  sm: '1.5rem',
                  md: '1.5rem',
                },
              }}>
              Distance
            </Typography>
            <Typography
              variant="h2"
              sx={{
                color: '#F9E719',
                fontWeight: 'bold',
                fontSize: {
                  xs: '1rem',
                  sm: '1.5rem',
                  md: '3rem',
                },
              }}
            >
              {distance ? (
                distance === 'Arrived' ? (
                  'Arrived'
                ) : (
                  `${distance} km`
                )
              ) : (
                <CircularProgress color="inherit" />
              )}
            </Typography>

          </Box>
        </Box>
      </Box>

      <Box
        sx={{
          fontFamily: 'Roboto',
          fontWeight: 400,
          fontStyle: 'normal',
          fontSize: '16px',
          lineHeight: '100%',
          color: '#000000',
          padding: "16px",
          alignItems: "center",
          display: "flex",
          gap: "10px"
        }}
      >
        Powered by
        <img src={PoweredBy_Logo} alt="Powered by logo" />
      </Box>
    </Box>
  );
};

export default NextStopTableScreenScreen;